import { StandardProps } from "@/types/types";
import JLDSPagination from "@JohnLewisPartnership/jl-design-system/dist/components/pagination/Pagination";
import { dispatchLegacyAnalyticsEvent } from "@/utils/analytics";
import { sanitiseQueryParams } from "@/utils/queryUtils";
import { getAnalyticsPageType } from "@/utils/setJLData";
import { useEffect, useRef } from "react";
import classNames from "classnames/bind";
import styles from "./Pagination.module.scss";
import { useFeatureFlags } from "@/flags/flags";
import { useRouteParams } from "@/hooks/useRouteParams";
import { PAGINATION } from "@/utils/events";
import { SanitisingLink } from "@/components/Pagination/utils/SanitisingLink.tsx";

const cx = classNames.bind(styles);

export function Pagination({
  productListingData,
  configProps,
}: Pick<StandardProps, "productListingData" | "configProps">) {
  const flags = useFeatureFlags();
  const { query, queryWithoutPageOrChunk, pageType, facetId } =
    useRouteParams();

  const { results } = productListingData;

  const paginationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // JLDS Pagination does not allow setting aria-label on the nav element, so need to do it here
    if (paginationRef.current) {
      const nav = paginationRef.current.querySelector("nav");
      if (nav) {
        nav.setAttribute(
          "aria-label",
          `Product listing page ${configProps?.desktopOnly ? "above" : "below"} grid pagination`,
        );
      }
    }
  }, []);

  // not using pagesAvailable as page size is determined by chunk, and results in the total pages changing
  // based on the current chunk
  const totalPagesAvailable = Math.ceil(results / Number(flags.pageSize.value));

  if (totalPagesAvailable < 2) {
    return null;
  }

  const onLinkClicked = (pageNumber: number) => {
    dispatchLegacyAnalyticsEvent(PAGINATION, {
      pagetype: getAnalyticsPageType(pageType, productListingData.results),
      pageNum: pageNumber,
    });
  };

  const activePage = parseInt(
    (Array.isArray(query.page) ? query.page[0] : query.page) || "1",
  );
  const pathname = `/${pageType}/${facetId?.join("/") || ""}`;

  return (
    <div
      data-component="Pagination"
      className={cx("pagination", {
        "pagination--desktopOnly": !!configProps?.desktopOnly,
        "pagination--centred": !!configProps?.centred,
        "pagination--right": !!configProps?.right,
      })}
      ref={paginationRef}
    >
      <JLDSPagination
        activePage={activePage}
        totalPages={totalPagesAvailable}
        onSelect={(pageNumber: number) => onLinkClicked(pageNumber)}
        location={{
          pathname,
          search: sanitiseQueryParams(queryWithoutPageOrChunk),
        }}
        LinkComponent={SanitisingLink}
        className={cx({
          "pagination--centred": !!configProps?.centred,
          "pagination--right": !!configProps?.right,
        })}
      />
    </div>
  );
}
